import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Formik } from "formik";
import sortBy from "lodash/sortBy";
import { Box } from "@mui/material";

import JoinCampaignTitleHeader from "@/features/campaigns/components/joinCampaignCommon/JoinCampaignTitleHeader";
import RRDEligibleLocations from "./eligible-locations/RRDEligibleLocations";
import RRDNotEligibleLocations from "./RRDNotEligibleLocations";
import RRDTotalBudget from "./eligible-locations/RRDTotalBudget";
import RRDSubmit from "./RRDSubmit";
import { GlobalContext } from "@/helpers/contexts/contexts";
import useIsParticipant from "@/features/auth/hooks/useIsParticipant";
import TabPanel from "@/components/muiCustom/tabs/TabPanel";
import { rrdDirectMailCampaignHelper } from "../utils/rrdDirectMailHelper";
import { RpConfirmDialog } from "@/helpers/utils";
import RpConfirm from "@/components/RpConfirm";
import RpAlert from "@/components/alerts/RpAlert";
import JoinCampaignTopConfirm from "@/features/campaigns/components/joinCampaignCommon/JoinCampaignTopConfirm";
import RRDSubmitTop from "./RRDSubmitTop";
import PreviewOnlyDialog from "@/features/campaigns/components/joinCampaignCommon/PreviewOnlyDialog";

type RRDRecord = Record<string, any>;

type RRDJoinCampaignFormProps = {
  campaign?: RRDRecord;
  initialValues?: RRDRecord;
  onSubmit?: (_data: RRDRecord) => void;
  drafttedOrderIds?: any[];
};

const FormikForm = Formik as React.ComponentType<Record<string, any>>;
const ConfirmDialog = RpConfirmDialog as any;

function RRDJoinCampaignForm({
  campaign,
  initialValues,
  onSubmit,
  drafttedOrderIds,
}: RRDJoinCampaignFormProps) {
  const { loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as RRDRecord;
  const isPreview = !useIsParticipant();
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [expanded, setExpanded] = useState<any[]>([]);

  const validLocations = useMemo(() => {
    const { eligible_location } = campaign || {};
    return eligible_location && eligible_location.length
      ? sortBy(eligible_location, "id")
      : [];
  }, [campaign]);

  const inValidLocationsCount = useMemo(() => {
    if (!campaign) return 0;

    return campaign?.not_eligible_locations?.length;
  }, [campaign]);

  const allLocationsIds = useMemo(() => {
    const { not_eligible_locations } = campaign || {};

    if (selectedTab === 0) {
      return validLocations && validLocations.length
        ? validLocations.map((l: RRDRecord) => l?.id)
        : [];
    } else if (selectedTab === 1) {
      return not_eligible_locations && not_eligible_locations.length
        ? not_eligible_locations.map((l: RRDRecord) => l?.id)
        : [];
    }
  }, [campaign, selectedTab, validLocations]);

  useEffect(() => {
    if (validLocations?.length === 0 && inValidLocationsCount) {
      setSelectedTab(1);
    }
  }, [validLocations?.length, inValidLocationsCount]);

  useEffect(() => {
    if (Array.isArray(allLocationsIds) && allLocationsIds?.length) {
      if (allLocationsIds.length <= 5) {
        setExpanded([...allLocationsIds]);
      } else {
        setExpanded([allLocationsIds[0]]);
      }
    } else {
      setExpanded([]);
    }
  }, [allLocationsIds]);

  const getDataToSubmit = useCallback(
    (
      locations: RRDRecord[],
      is_draft: boolean,
      totalBudget: any,
      removeOrderFromDraftOrderIds: any[] = []
    ) => {
      return {
        is_draft,
        selections: locations,
        adminMinimumCouponDurationDays:
          campaign?.adminMinimumCouponDurationDays,
        adminCouponExpirationDate: campaign?.adminCouponExpirationDate,
        adminCouponExpirationMode:
          rrdDirectMailCampaignHelper.getCouponExpirationSetBy(campaign),
        adminFixedCouponDurationDays:
          rrdDirectMailCampaignHelper.getFixedCouponDurationDays(campaign),
        couponExpirationSetBy:
          rrdDirectMailCampaignHelper.getCouponExpirationSetBy(campaign),
        fixedCouponDurationDays:
          rrdDirectMailCampaignHelper.getFixedCouponDurationDays(campaign),

        brandId: selectedBrand?.id,
        userId: loggedInUser?.id,
        participantId: loggedInUser?.participantData?.id,
        sysParticipantId:
          loggedInUser?.participantData?.system_generated_participant_id,
        campaignId: campaign?.campaignId,
        sysCampaignId: campaign?.systemGeneratedCampaignId,
        campaignName: campaign?.campaign_name,
        campaignType: campaign?.campaign_type,
        paymentMethod: campaign?.paymentMethod,
        totalBudget,
        removeOrderFromDraftOrderIds,
      };
    },
    [
      campaign,
      selectedBrand?.id,
      loggedInUser?.id,
      loggedInUser?.participantData?.id,
      loggedInUser?.participantData?.system_generated_participant_id,
    ]
  );

  const handleFormSubmit = useCallback(
    (values: RRDRecord, actions: RRDRecord) => {
      if (isPreview) {
        actions.setSubmitting(false);
        setPreviewDialogOpen(true);
        return;
      }
      const { is_draft, ...locations } = values;
      if (is_draft) {
        actions.setFieldValue("is_draft", false);
      }

      actions.setSubmitting(false);

      const fullyFilledLocations =
        rrdDirectMailCampaignHelper.fetchFullyFilledLocations(locations);

      const formattedLocations =
        rrdDirectMailCampaignHelper.formatSelectedLocations(
          fullyFilledLocations,
          campaign
        );

      const totalBudget =
        rrdDirectMailCampaignHelper.getLocationsTotalBudget(
          fullyFilledLocations
        );

      let removeOrderFromDraftOrderIds = [];
      if (Array.isArray(drafttedOrderIds) && drafttedOrderIds?.length) {
        removeOrderFromDraftOrderIds = drafttedOrderIds?.filter(
          (id) =>
            !fullyFilledLocations?.some((loc: RRDRecord) => loc?.orderId === id)
        );
      }

      const dataToSubmit = getDataToSubmit(
        formattedLocations,
        is_draft,
        totalBudget,
        removeOrderFromDraftOrderIds
      );

      if (validLocations.length === fullyFilledLocations.length) {
        onSubmit?.(dataToSubmit);
        actions.setSubmitting(false);
      } else {
        ConfirmDialog(
          <RpConfirm
            message={`You're enrolling ${fullyFilledLocations.length} of ${validLocations.length} locations in this campaign. Are you sure you want to proceed?`}
          />,
          () => {
            onSubmit?.(dataToSubmit);
            actions.setSubmitting(false);
            return;
          },
          () => {
            actions.setSubmitting(false);
            return;
          }
        );
      }
    },
    [
      campaign,
      drafttedOrderIds,
      getDataToSubmit,
      isPreview,
      onSubmit,
      validLocations.length,
    ]
  );

  const renderPage = ({ handleSubmit }: RRDRecord) => {
    return (
      <form onSubmit={handleSubmit} className="pt-3">
        <JoinCampaignTopConfirm backLinkUrl="/launch-direct-mail">
          {selectedTab === 0 && <RRDSubmitTop isPreview={isPreview} />}
        </JoinCampaignTopConfirm>
        <div className="join-campaign-form mx-lg-5 mx-3">
          <JoinCampaignTitleHeader
            campaign_name={campaign?.campaign_name}
            setSelectedTab={setSelectedTab}
            selectedTab={selectedTab}
            validLocationsCount={validLocations?.length}
            inValidLocationsCount={inValidLocationsCount}
            setExpanded={setExpanded}
            expanded={expanded}
            allLocationsIds={allLocationsIds}
          />

          <TabPanel value={selectedTab} index={0}>
            {validLocations.length > 0 ? (
              <>
                <RRDEligibleLocations
                  locations={validLocations}
                  campaign={campaign}
                  expanded={expanded}
                  setExpanded={setExpanded}
                />
                <RRDTotalBudget />
                <RRDSubmit
                  isPreview={isPreview}
                  totalLocationsCount={validLocations?.length}
                />
              </>
            ) : (
              <Box
                sx={{ p: 2, mt: 20, display: "flex", justifyContent: "center" }}
              >
                <RpAlert severity="info">
                  There are no Eligible Locations
                </RpAlert>
              </Box>
            )}
          </TabPanel>
          <TabPanel value={selectedTab} index={1}>
            <RRDNotEligibleLocations
              locations={campaign?.not_eligible_locations}
            />
          </TabPanel>
        </div>
      </form>
    );
  };

  return (
    <div className="position-relative pt-5 pt-sm-0">
      <FormikForm
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleFormSubmit}
      >
        {(props: RRDRecord) => renderPage(props)}
      </FormikForm>
      <PreviewOnlyDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
      />
    </div>
  );
}

export default RRDJoinCampaignForm;
